import { Link } from 'react-router-dom';
import {
  Users,
  Link2,
  TrendingUp,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AdminLayout } from '@/components/admin';
import { Card, CardTitle, Button } from '@/components/ui';
import { useAdminStats } from '@/hooks/useAdminStats';

const AdminDashboard = () => {
  const { t, i18n } = useTranslation();
  const { stats, loading, refresh } = useAdminStats();

  const statsCards = [
    {
      label: t('admin.totalUsers'),
      value: stats?.totalUsers ?? 0,
      icon: <Users className="w-5 h-5" />,
      color: 'bg-blue-100 text-blue-600',
      href: '/admin/users',
    },
    {
      label: t('admin.totalDeeplinks'),
      value: stats?.totalDeeplinks ?? 0,
      icon: <Link2 className="w-5 h-5" />,
      color: 'bg-lime-100 text-lime-600',
      href: '/admin/deeplinks',
    },
    {
      label: t('admin.usersToday'),
      value: stats?.usersToday ?? 0,
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'bg-green-100 text-green-600',
      href: '/admin/users',
    },
    {
      label: t('admin.deeplinksToday'),
      value: stats?.deeplinksToday ?? 0,
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'bg-purple-100 text-purple-600',
      href: '/admin/deeplinks',
    },
  ];

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-dark-800 dark:text-white mb-2">
            {t('admin.dashboardTitle')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400">{t('admin.dashboardSubtitle')}</p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<RefreshCw className="w-4 h-4" />}
          onClick={refresh}
          disabled={loading}
        >
          {t('common.refresh')}
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
        {statsCards.map((stat, index) => (
          <Link key={index} to={stat.href}>
            <Card variant="default" padding="md" className="hover:shadow-md transition-shadow cursor-pointer">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${stat.color}`}>
                  {stat.icon}
                </div>
              </div>
              {loading ? (
                <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
              ) : (
                <p className="text-2xl font-bold text-dark-800 dark:text-white">
                  {stat.value.toLocaleString(i18n.language)}
                </p>
              )}
              <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
            </Card>
          </Link>
        ))}
      </div>

      {/* Quick Links */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Manage Users */}
        <Card variant="default" padding="md">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/40 rounded-2xl flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <CardTitle>{t('admin.manageUsers')}</CardTitle>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.manageUsersDescription')}</p>
            </div>
          </div>
          <Link to="/admin/users">
            <Button variant="secondary" size="sm" rightIcon={<ArrowRight className="w-4 h-4" />}>
              {t('admin.viewAllUsers')}
            </Button>
          </Link>
        </Card>

        {/* Manage Deeplinks */}
        <Card variant="default" padding="md">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-lime-100 dark:bg-lime-900/40 rounded-2xl flex items-center justify-center">
              <Link2 className="w-6 h-6 text-lime-600 dark:text-lime-400" />
            </div>
            <div>
              <CardTitle>{t('admin.manageDeeplinks')}</CardTitle>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.manageDeeplinksDescription')}</p>
            </div>
          </div>
          <Link to="/admin/deeplinks">
            <Button variant="secondary" size="sm" rightIcon={<ArrowRight className="w-4 h-4" />}>
              {t('admin.viewAllDeeplinks')}
            </Button>
          </Link>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
